import { useMemo } from 'react';
import { ProductSearchResultExpanded } from '../../../shared/classes/ProductSearchResultExpanded';

type RenderItem =
    | { kind: 'product'; key: string; product: ProductSearchResultExpanded }
    | { kind: 'promo'; key: string; promo: React.ReactNode };

interface UsePromoBlocksProps {
    enablePromoBlocks: boolean;
    repeat?: boolean;
    startPosition?: number;
    frequency?: number;
    promoBlocks?: React.ReactNode[];
    products: ProductSearchResultExpanded[];
}

export default function usePromoBlocks({
    enablePromoBlocks,
    promoBlocks,
    startPosition,
    frequency,
    products,
    repeat
}: UsePromoBlocksProps) {
    const renderItems = useMemo<RenderItem[]>(() => {
        const firstPromoPosition = startPosition ?? 0;
        const promoFrequency = frequency ?? 0;
        const totalPromoBlocks = Array.isArray(promoBlocks) ? promoBlocks.length : 0;

        const hasValidPromoConfig =
            enablePromoBlocks && Array.isArray(promoBlocks) && totalPromoBlocks > 0 && firstPromoPosition > 0 && promoFrequency > 0;

        let renderedPromoCount = 0;
        const items: RenderItem[] = [];

        for (let index = 0; index < products.length; index += 1) {
            const product = products[index];
            const positionInProducts = index + 1;

            items.push({
                kind: 'product',
                key: `product-${product.RecordId}`,
                product
            });

            const shouldInsertPromoAfterThisProduct =
                hasValidPromoConfig &&
                positionInProducts >= firstPromoPosition &&
                (positionInProducts - firstPromoPosition) % promoFrequency === 0;

            if (!shouldInsertPromoAfterThisProduct) {
                continue;
            }

            if (repeat) {
                const promo = promoBlocks[renderedPromoCount % totalPromoBlocks];
                const promoKey = `promo-${renderedPromoCount}-after-${product.RecordId}`;

                renderedPromoCount += 1;
                items.push({ kind: 'promo', key: promoKey, promo });
                continue;
            }

            if (renderedPromoCount < totalPromoBlocks) {
                const promo = promoBlocks[renderedPromoCount];
                const promoKey = `promo-${renderedPromoCount}-after-${product.RecordId}`;

                renderedPromoCount += 1;
                items.push({ kind: 'promo', key: promoKey, promo });
            }
        }

        return items;
    }, [enablePromoBlocks, promoBlocks, startPosition, frequency, products, repeat]);

    return {
        renderItems
    };
}
